import { UserId } from '@business/entities/account';
import { CommonService } from '@business/services';
import { FieldProps, FormControl, useFormField } from '@core/utils/form';
import { R } from '@core/utils/r';
import FormHelperText from '@material-ui/core/FormHelperText';
import { GCDropdown } from '@modules/common/GCDropdown';
import React, { CSSProperties, useMemo } from 'react';
import Select, { StylesConfig } from 'react-select';
import { ProjectUserResponse } from '@business/entities/project';
import { AvatarGroupProject, AvatarGroupProjectItem } from '@modules/common/AvatarGroup';

const selectStyles: StylesConfig<any, false> = {
  control: provided => ({
    ...provided,
    minWidth: 240,
    margin: 8,
  }),
  menu: () => ({ boxShadow: 'inset 0 1px 0 rgba(0, 0, 0, 0.1)' }),
  menuPortal: base => {
    const { zIndex, ...rest } = base;
    return { ...rest, zIndex: 9999 };
  },
};

// eslint-disable-next-line @typescript-eslint/no-unused-vars
export interface FormUserAvatarBaseProps<T extends string[]> {
  onChange?(value: T | null): void;
  autofocus?: boolean;
  style?: CSSProperties;
}

export interface FormUserAvatarProjectProps<T extends string[]> extends FormUserAvatarBaseProps<string[]>, FieldProps<FormControl<string[]>> {
  users: ProjectUserResponse[];
  onAdd?(value: string): void;
  tooltipContainer?: string;
  maxSize?: number;
  additionalOptions?: { label: string; value: string; id: string }[];
  itemKey?: 'email' | 'id';
  showClients?: boolean;
  container?: string;
}

export function FormUserAvatarProject(props: FormUserAvatarProjectProps<string[]>) {
  const { additionalOptions = [], users, itemKey = 'email' } = props;
  const { formControl, fieldState } = useFormField(props);
  const fieldTouched = (fieldState && fieldState.touched) || formControl.inheritedSubmitted || false;
  const fieldError = (fieldTouched && fieldState.errorMessage) || null;
  const fieldValue = (R.isNil(fieldState.value) ? [] : R.filter(fieldState.value, u => u != 0)) as UserId[];

  const selectedItems: AvatarGroupProjectItem[] = useMemo(() => {
    return R.pipe(
      fieldValue,
      R.ops.map(id => {
        const additioanlItem = R.find(additionalOptions, o => o.value === id);
        if (additioanlItem) return additioanlItem;

        return R.find(users, u => R.get(u, itemKey) === id);
      }),
      R.ops.compact,
      R.ops.map(item => {
        const isClient = R.get(item, 'isClient') || false;
        return {
          id: item.id,
          title: R.get(item, 'name'),
          isClient: isClient,
        };
      }),
    );
  }, [fieldValue, additionalOptions, users, itemKey]);

  const items = useMemo(() => {
    const exceptUsers = users.filter(u => !fieldValue.includes(R.get(u, itemKey)));

    if (props.onAdd) {
      return CommonService.selectGroupedItems(
        [
          {
            label: 'Staff',
            items: CommonService.selectItems(
              exceptUsers.filter(f => !f.isClient),
              'name',
              itemKey,
            ),
          },
          {
            label: 'Clients',
            items: CommonService.selectItems(
              exceptUsers.filter(f => f.isClient),
              'name',
              itemKey,
            ),
          },
          {
            label: 'New client',
            items: [{ label: 'Invite client', value: '-00' }],
          },
        ],
        'label',
        s => s.items,
        'label',
        'value',
      );
    }

    return CommonService.selectItems(exceptUsers, 'name', itemKey);
  }, [fieldValue, itemKey, props.onAdd, users]);

  const content = (
    <React.Fragment>
      <AvatarGroupProject
        showClients={props.showClients}
        overlap
        size={2}
        items={selectedItems}
        tooltipContainer={props.tooltipContainer}
        onRemove={(_, index) => {
          const newValues = fieldValue.filter((_, inIndex) => index !== inIndex);
          formControl.setValue(newValues);
          props.onChange && props.onChange(newValues);
        }}
      >
        {((props.maxSize && props.maxSize > fieldValue.length) || !props.maxSize) && (
          <GCDropdown
            // container={props.container}
            target={
              <div className="avatar-group-item" style={{ marginLeft: '-0.8rem' }} key={'_avatar_' + formControl.uid + 'new'}>
                <div className="d-inline-block" id={'user_item_new' + formControl.uid}>
                  <div className="avatar-md" style={{ width: '2rem', height: '2rem' }}>
                    <span
                      className={`avatar-title rounded-circle text-white font-size-12`}
                      style={{ backgroundColor: '#FFF', border: '1.5px dashed #3e3e3e' }}
                    >
                      <i style={{ color: '#3e3e3e' }} className="fa fa-user-plus"></i>
                    </span>
                  </div>
                </div>
              </div>
            }
          >
            {setOpen => (
              <Select
                autoFocus
                backspaceRemovesValue={false}
                components={{ IndicatorSeparator: null }}
                controlShouldRenderValue={false}
                hideSelectedOptions={false}
                isClearable={false}
                menuIsOpen
                options={items}
                placeholder="Search..."
                isSearchable
                styles={selectStyles}
                tabSelectsValue={false}
                onChange={value => {
                  if (value) {
                    const selectedValue = R.toString(R.get(value, 'value'));
                    if (selectedValue.includes('-00')) {
                      props.onAdd && props.onAdd(selectedValue);
                      setOpen(false);
                      return;
                    }
                    if (fieldValue.length == 0) {
                      setOpen(false);
                    }
                    props.onChange && props.onChange([...fieldValue, selectedValue]);
                    formControl.setValue([...fieldValue, selectedValue] as any);
                  }
                }}
              />
            )}
          </GCDropdown>
        )}
      </AvatarGroupProject>

      {!!fieldError && <FormHelperText style={{ color: '#f44336' }}>{fieldError}</FormHelperText>}
    </React.Fragment>
  );

  return content;
}
